import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Facebook, Youtube, Phone, Mail, Clock, ShieldCheck } from "lucide-react";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative mt-20 border-t border-[#F7C7DC] bg-gradient-to-b from-[#FFF0F4] via-[#FCE8EE] to-[#F7DCE4]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-10">
        
        {/* Main Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          
          {/* Column 1: Brand Info */}
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="ALAIRA HOUSE Logo" className="w-12 h-12 rounded-full object-cover shadow-md border-2 border-white" />
              <span className="font-display font-bold text-[#1C1924] text-xl tracking-wide">
                ALAIRA <span className="text-[#E5497C] font-semibold italic font-serif-italic">HOUSE</span>
              </span>
            </div>
            <p className="text-[13.5px] text-[#4A4652] max-w-sm leading-relaxed font-body">
              Thoughtful, hand-curated gifts for the couples who love across cities, countries, and time zones. Every parcel is love, delivered.
            </p>
            <div className="flex items-center gap-2.5 pt-2">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/alairaluxe?igsh=cnJ6anh4cXExMHRp" },
                { Icon: Twitter, href: "#" },
                { Icon: Facebook, href: "#" },
                { Icon: Youtube, href: "#" }
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  aria-label="social"
                  data-testid={`footer-social-${i}`}
                  className="w-9 h-9 rounded-full bg-white border border-[#F7C7DC] text-[#1C1924] hover:bg-[#E5497C] hover:text-white hover:border-[#E5497C] transition-all duration-300 flex items-center justify-center shadow-sm"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="lg:col-span-2 lg:ml-4">
            <h4 className="font-display text-[13px] font-bold text-[#E5497C] uppercase tracking-widest mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3 text-[13.5px] text-[#3A3642] font-body">
              <li><Link to="/" className="hover:text-[#E5497C] transition font-medium">Home</Link></li>
              <li><Link to="/shop" className="hover:text-[#E5497C] transition font-medium">Shop Gifts</Link></li>
              <li><Link to="/about" className="hover:text-[#E5497C] transition font-medium">About Us</Link></li>
              <li><Link to="/profile" className="hover:text-[#E5497C] transition font-medium">My Account</Link></li>
              <li><Link to="/contact" className="hover:text-[#E5497C] transition font-medium">Contact</Link></li>
            </ul>
          </div>

          {/* Column 3: Occasions */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-[13px] font-bold text-[#E5497C] uppercase tracking-widest mb-4">
              Occasions
            </h4>
            <ul className="space-y-2.5 text-[13.5px] text-[#3A3642] font-body">
              <li><Link to="/shop?occasion=congratulations" className="hover:text-[#E5497C] transition font-medium">Congratulations</Link></li>
              <li><Link to="/shop?occasion=birthday" className="hover:text-[#E5497C] transition font-medium">Birthday</Link></li>
              <li><Link to="/shop?occasion=anniversary" className="hover:text-[#E5497C] transition font-medium">Anniversary</Link></li>
              <li><Link to="/shop?occasion=wedding" className="hover:text-[#E5497C] transition font-medium">Wedding</Link></li>
              <li><Link to="/shop?occasion=festival" className="hover:text-[#E5497C] transition font-medium">Rakhi & Festivals</Link></li>
              <li><Link to="/shop?occasion=romance" className="hover:text-[#E5497C] transition font-medium">Valentine's & Romance</Link></li>
              <li><Link to="/shop?occasion=graduation" className="hover:text-[#E5497C] transition font-medium">Graduation</Link></li>
              <li><Link to="/shop?occasion=housewarming" className="hover:text-[#E5497C] transition font-medium">Housewarming</Link></li>
              <li><Link to="/shop?occasion=get-well" className="hover:text-[#E5497C] transition font-medium">Get Well Soon</Link></li>
              <li><Link to="/shop?occasion=thank-you" className="hover:text-[#E5497C] transition font-medium">Thank You</Link></li>
            </ul>
          </div>

          {/* Column 4: Get In Touch & Assurance */}
          <div className="lg:col-span-4 space-y-4">
            <h4 className="font-display text-[13px] font-bold text-[#E5497C] uppercase tracking-widest mb-4">
              Get In Touch
            </h4>
            <ul className="space-y-3 text-[13.5px] text-[#3A3642] font-body">
              <li className="flex items-center gap-2.5">
                <Phone size={14} className="text-[#E5497C] shrink-0" />
                <span className="font-medium">WhatsApp us anytime</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail size={14} className="text-[#E5497C] shrink-0" />
                <a href="mailto:alairamaison@gmail.com" className="hover:text-[#E5497C] transition font-medium">
                  alairamaison@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Instagram size={14} className="text-[#E5497C] shrink-0" />
                <a href="https://www.instagram.com/alairaluxe?igsh=cnJ6anh4cXExMHRp" target="_blank" rel="noopener noreferrer" className="hover:text-[#E5497C] transition font-medium">@alairaluxe</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Clock size={14} className="text-[#E5497C] shrink-0" />
                <span className="font-medium">Mon–Sat, 9am – 7pm</span>
              </li>
            </ul>

            {/* Payments Badges */}
            <div className="pt-2 flex flex-wrap items-center gap-1.5 text-[9px] uppercase tracking-widest font-bold text-[#8b8790] font-body">
              <span className="px-2.5 py-1 border border-[#F7C7DC] rounded-full bg-white/80 shadow-sm text-[#1C1924]">Visa</span>
              <span className="px-2.5 py-1 border border-[#F7C7DC] rounded-full bg-white/80 shadow-sm text-[#1C1924]">MC</span>
              <span className="px-2.5 py-1 border border-[#F7C7DC] rounded-full bg-white/80 shadow-sm text-[#1C1924]">Amex</span>
              <span className="px-2.5 py-1 border border-[#F7C7DC] rounded-full bg-white/80 shadow-sm text-[#1C1924]">UPI</span>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-6 border-t border-[#F7C7DC] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[13px] text-[#6A6472] font-body">
            © {new Date().getFullYear()} Alaira House. All rights reserved.
          </p>

          <div className="text-[13px] text-[#E5497C] font-bold tracking-wide flex items-center gap-1 font-body">
            ✦ Because Every Gift Tells a Story ✦
          </div>

          <div className="flex gap-4 text-[13px] text-[#6A6472] font-body">
            <a href="#" className="hover:text-[#E5497C] transition">Privacy Policy</a>
            <a href="#" className="hover:text-[#E5497C] transition">Terms</a>
            <a href="#" className="hover:text-[#E5497C] transition">Shipping</a>
          </div>
        </div>

      </div>
    </footer>
  );
}
